// Calls to the Worker's /v1 API (docs/architecture/api.md). Same origin: the session is the
// HttpOnly `pf_session` cookie, and every request carries X-PF-Schema (also the CSRF guard).
import { ErrorBody, SCHEMA_HEADER, SCHEMA_VERSION } from '@pf/api-types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  // The dev trip simulator answers instead of the server (IMPL-CONNECT S7).
  if (import.meta.env.DEV) {
    const sim = await import('./dev/sim.ts');
    await sim.resume();
    if (sim.simulating()) {
      const answer = await sim.answer(path, init);
      if (answer === null) throw new ApiError(0, 'offline', 'Not available in the simulator.');
      return answer as T;
    }
  }
  let res: Response;
  try {
    res = await fetch(`/v1${path}`, {
      method: init.method ?? 'GET',
      credentials: 'same-origin',
      headers: {
        [SCHEMA_HEADER]: String(SCHEMA_VERSION),
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      body: init.body === undefined ? null : JSON.stringify(init.body),
    });
  } catch {
    throw new ApiError(0, 'offline', "You're offline. Reconnect to keep editing.");
  }
  if (res.status === 204) return undefined as T;
  const json: unknown = await res.json().catch(() => undefined);
  if (!res.ok) {
    const parsed = ErrorBody.safeParse(json);
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.error.code : 'internal',
      parsed.success ? parsed.data.error.message : `Something went wrong (${res.status}).`,
    );
  }
  return json as T;
}

/** Raw bytes to the Worker (attachments: PUT /attachments/:id). */
export async function upload(path: string, file: Blob): Promise<void> {
  const res = await fetch(`/v1${path}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      [SCHEMA_HEADER]: String(SCHEMA_VERSION),
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  }).catch(() => null);
  if (!res) throw new ApiError(0, 'offline', "You're offline. Reconnect to upload.");
  if (!res.ok) {
    const parsed = ErrorBody.safeParse(await res.json().catch(() => undefined));
    throw new ApiError(
      res.status,
      parsed.success ? parsed.data.error.code : 'internal',
      parsed.success ? parsed.data.error.message : `Upload failed (${res.status}).`,
    );
  }
}

/** A stable id for this browser, so "sign out on this device" works (FR-AUTH-09). */
export function deviceId(): string {
  const key = 'pf.device.id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}
